import { useQuery } from '@sequencehq/api'
import {
  dashboardv99990101Client,
  Dashboardv99990101Api
} from '@sequencehq/api/dashboard/v99990101'
import { format } from 'date-fns'
import { useMemo } from 'react'

type CurrencyTotals =
  Dashboardv99990101Api.GetInvoiceSummary.InvoiceSummaryCurrencyTotals

export type InvoiceSummaryStat = {
  label: string
  totals: CurrencyTotals[]
  isLoading: boolean
  emphasize: boolean
  testId: string
}

type InvoiceSummaryFilter =
  | { customerId: string }
  | { billingScheduleId: string }

const toStat = (
  query: {
    data?: { totalsByCurrency: CurrencyTotals[] } | null
    isPending: boolean
  },
  label: string,
  testId: string,
  emphasize = false
): InvoiceSummaryStat => ({
  label,
  totals: query.data?.totalsByCurrency ?? [],
  isLoading: query.isPending,
  emphasize,
  testId
})

/**
 * Fetches the four fixed invoice summaries. Each card has its own status
 * filter, scoped to either a customer or a billing schedule. "Overdue" has no
 * native status on the endpoint — it is derived from outstanding invoices
 * whose due date has passed.
 */
export const useInvoiceSummaryStats = (
  filter: InvoiceSummaryFilter,
  testIdPrefix: string
): InvoiceSummaryStat[] => {
  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])

  const draft = useQuery(dashboardv99990101Client.getInvoiceSummary, {
    ...filter,
    invoiceStatus: 'DRAFT'
  })
  const outstanding = useQuery(dashboardv99990101Client.getInvoiceSummary, {
    ...filter,
    invoiceStatus: 'SENT',
    invoicePaymentStatus: 'UNPAID'
  })
  const overdue = useQuery(dashboardv99990101Client.getInvoiceSummary, {
    ...filter,
    invoiceStatus: 'SENT',
    invoicePaymentStatus: 'UNPAID',
    dueBefore: today
  })
  const paid = useQuery(dashboardv99990101Client.getInvoiceSummary, {
    ...filter,
    invoicePaymentStatus: 'PAID'
  })

  return [
    toStat(draft, 'Draft invoices', `${testIdPrefix}.draft`),
    toStat(outstanding, 'Outstanding invoices', `${testIdPrefix}.outstanding`),
    toStat(overdue, 'Overdue invoices', `${testIdPrefix}.overdue`, true),
    toStat(paid, 'Paid invoices', `${testIdPrefix}.paid`)
  ]
}
